import { useState } from 'react'
import { useDisclosure, useToast } from '@chakra-ui/react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCurrentVisits,
  deleteVisit,
  sortVisits
} from '../../../reducers/currentVisitsReducer'
import { getVisitService } from '../../../services/Services'
import { useParams } from 'react-router-dom'
import { type DossierPreferences } from '../../../client/modules/account/AccountProvider'

interface UseVisitActionsOptions {
  preferences: DossierPreferences
  onPreferencesChange: (preferences: DossierPreferences) => void
}

export function useVisitActions({
  preferences,
  onPreferencesChange
}: UseVisitActionsOptions) {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const currentVisits = useAppSelector(selectCurrentVisits)
  const { patientId } = useParams()

  // Modal state
  const {
    isOpen: isVisitViewModalOpen,
    onOpen: onVisitViewModalOpen,
    onClose: onVisitViewModalClose
  } = useDisclosure()

  const {
    isOpen: isVisitFormModalOpen,
    onOpen: onVisitFormModalOpen,
    onClose: onVisitFormModalClose
  } = useDisclosure()

  const {
    isOpen: isPrescriptionModalOpen,
    onOpen: onPrescriptionModalOpen,
    onClose: onPrescriptionModalClose
  } = useDisclosure()

  // Visit interaction state
  const [currentVisit, setCurrentVisit] = useState<any>(null)
  const [followUpFlag, setFollowUpFlag] = useState(false)

  // Display state
  const [showCharts, setShowCharts] = useState(false)
  const { displayMode, sortOrder } = preferences

  // Visit CRUD handlers
  const handleVisitDelete = (visitId: string, reason = '') => {
    if (!patientId) return
    if (!reason?.trim()) return
    const revision =
      currentVisits.find((visit) => visit.id === visitId)?.revision ?? 0
    getVisitService()
      .deleteVisit(patientId, visitId, revision, reason)
      .then(() => {
        dispatch(deleteVisit(visitId))
      })
      .catch((error) => toast({ title: error.message, status: 'error' }))
  }

  const handleVisitEdit = (visitId: string) => {
    const filteredVisit = currentVisits.find((visit) => visit.id === visitId)
    if (!filteredVisit) return
    setCurrentVisit(filteredVisit)
    setFollowUpFlag(filteredVisit.isFollowUp)
    onVisitFormModalOpen()
  }

  const handleVisitSelect = (visitId: string) => {
    setCurrentVisit(currentVisits.find((visit) => visit.id === visitId))
    onVisitViewModalOpen()
  }

  // Display toggles
  const toggleDisplayMode = () => {
    onPreferencesChange({
      ...preferences,
      displayMode: displayMode === 'TIMELINE' ? 'TABLE' : 'TIMELINE'
    })
  }

  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC'
    onPreferencesChange({ ...preferences, sortOrder: newOrder })
    dispatch(sortVisits(newOrder))
  }

  const toggleShowCharts = () => {
    setShowCharts(!showCharts)
  }

  // Action bar helpers
  const handleAddVisit = () => {
    setFollowUpFlag(false)
    setCurrentVisit(null)
    onVisitFormModalOpen()
  }

  const handleAddFollowUp = () => {
    setCurrentVisit(null)
    setFollowUpFlag(true)
    onVisitFormModalOpen()
  }

  return {
    // Modal state
    isVisitViewModalOpen,
    onVisitViewModalOpen,
    onVisitViewModalClose,
    isVisitFormModalOpen,
    onVisitFormModalClose,
    isPrescriptionModalOpen,
    onPrescriptionModalOpen,
    onPrescriptionModalClose,
    // Visit interaction
    currentVisit,
    followUpFlag,
    handleVisitSelect,
    handleVisitEdit,
    handleVisitDelete,
    // Display state
    displayMode,
    sortOrder,
    showCharts,
    toggleDisplayMode,
    toggleSortOrder,
    toggleShowCharts,
    // Action bar
    handleAddVisit,
    handleAddFollowUp
  }
}
